<script>
import axios from "axios";
import Swal from "sweetalert2";
import { mapGetters } from "vuex";

export default {
  name: "ScholarshipMgt",
  data() {
    return {
      scholarships: [],
      selected: null,
      searchQuery: "",
      searchTimer: null,
      status: "",
      dateRange: { start: "", end: "" },
      pagination: {
        currentPage: 1,
        itemsPerPage: 10,
        totalItems: 0,
        totalPages: 1,
      },
      loading: false,
      exporting: false,
      initialized: false,
    };
  },
  computed: {
    ...mapGetters(["hasPermission"]),
    canManage() {
      return this.hasPermission("scholarship", "manage");
    },
    pageRange() {
      const total = this.pagination.totalPages;
      const current = this.pagination.currentPage;
      let start = Math.max(1, current - 2);
      const end = Math.min(total, start + 4);
      if (end - start < 4) start = Math.max(1, end - 4);
      return Array.from(
        { length: end - start + 1 },
        (_, index) => start + index
      );
    },
    paginationInfo() {
      const start = this.pagination.totalItems
        ? (this.pagination.currentPage - 1) * this.pagination.itemsPerPage + 1
        : 0;
      return {
        start,
        end: Math.min(
          this.pagination.currentPage * this.pagination.itemsPerPage,
          this.pagination.totalItems
        ),
        total: this.pagination.totalItems,
      };
    },
  },
  watch: {
    searchQuery() {
      if (!this.initialized) return;
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => this.load(1), 350);
    },
    status() {
      if (this.initialized) this.load(1);
    },
    "dateRange.start"() {
      if (this.initialized) this.load(1);
    },
    "dateRange.end"() {
      if (this.initialized) this.load(1);
    },
    "pagination.itemsPerPage"() {
      if (this.initialized) this.load(1);
    },
  },
  mounted() {
    this.initialized = true;
    this.load(1);
  },
  beforeUnmount() {
    window.clearTimeout(this.searchTimer);
  },
  methods: {
    params(page = 1, limit = this.pagination.itemsPerPage) {
      return {
        page,
        limit,
        search: this.searchQuery.trim() || undefined,
        status: this.status || undefined,
        startDate: this.dateRange.start || undefined,
        endDate: this.dateRange.end || undefined,
      };
    },
    async load(page = this.pagination.currentPage) {
      this.loading = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/scholarships/applications`,
          { params: this.params(page) }
        );
        this.scholarships = response.data.data.scholarships || [];
        const pagination = response.data.data.pagination || {};
        this.pagination.currentPage = pagination.page || page;
        this.pagination.totalPages = pagination.pages || 1;
        this.pagination.totalItems = pagination.total || 0;
      } catch (error) {
        this.showError(error, "Failed to fetch scholarship applications");
      } finally {
        this.loading = false;
      }
    },
    clearFilters() {
      this.initialized = false;
      this.searchQuery = "";
      this.status = "";
      this.dateRange = { start: "", end: "" };
      this.$nextTick(() => {
        this.initialized = true;
        this.load(1);
      });
    },
    name(item) {
      return (
        item.submittedSnapshot?.name ||
        [item.userId?.lastName, item.userId?.firstName]
          .filter(Boolean)
          .join(" ") ||
        "N/A"
      );
    },
    program(item) {
      return (
        item.submittedSnapshot?.programName ||
        item.applicationId?.programOfferingId?.program?.name ||
        "N/A"
      );
    },
    applicationNumber(item) {
      return (
        item.applicationId?.applicationNumber ||
        item.submittedSnapshot?.applicationNumber ||
        "N/A"
      );
    },
    formatDate(value) {
      if (!value) return "N/A";
      const date = new Date(value);
      return Number.isNaN(date.getTime())
        ? "N/A"
        : date.toLocaleDateString("en-GB");
    },
    statusLabel(value) {
      return String(value || "N/A")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    statusClass(status) {
      return (
        {
          pending: "text-bg-warning",
          accepted: "text-bg-success",
          rejected: "text-bg-danger",
        }[status] || "text-bg-secondary"
      );
    },
    async decide(item, status) {
      const result = await Swal.fire({
        title: `${this.statusLabel(status)} scholarship application?`,
        icon: status === "rejected" ? "warning" : "question",
        showCancelButton: true,
        confirmButtonText: this.statusLabel(status),
      });
      if (!result.isConfirmed) return;
      try {
        await axios.patch(
          `${process.env.VUE_APP_API_BASE_URL}/scholarships/applications/${item._id}`,
          { status }
        );
        this.selected = null;
        await this.load(this.pagination.currentPage);
        await Swal.fire(
          "Success",
          `Scholarship application ${status}`,
          "success"
        );
      } catch (error) {
        this.showError(error, "Failed to update scholarship status");
      }
    },
    async fetchAll() {
      const first = await axios.get(
        `${process.env.VUE_APP_API_BASE_URL}/scholarships/applications`,
        { params: this.params(1, 200) }
      );
      const rows = [...(first.data.data.scholarships || [])];
      const pages = first.data.data.pagination?.pages || 1;
      for (let page = 2; page <= pages; page += 1) {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/scholarships/applications`,
          { params: this.params(page, 200) }
        );
        rows.push(...(response.data.data.scholarships || []));
      }
      return rows;
    },
    exportRows(items) {
      return items.map((item) => ({
        "Scholarship Number": item.scholarshipNumber,
        Name: this.name(item),
        Email: item.userId?.email || item.submittedSnapshot?.email,
        Phone: item.userId?.phone || item.submittedSnapshot?.phone,
        "Application Number": this.applicationNumber(item),
        Programme: this.program(item),
        Address: item.address,
        "JAMB Registration": item.jambRegistrationNumber,
        "JAMB Score": item.jambScore,
        Status: this.statusLabel(item.status),
        Submitted: this.formatDate(item.createdAt),
      }));
    },
    async exportData(format) {
      this.exporting = true;
      try {
        const rows = this.exportRows(await this.fetchAll());
        if (!rows.length) {
          await Swal.fire(
            "No Data",
            "No scholarships match the current filters",
            "warning"
          );
          return;
        }
        if (format === "xlsx") {
          const [XLSX, { saveAs }] = await Promise.all([
            import("xlsx"),
            import("file-saver"),
          ]);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(
            workbook,
            XLSX.utils.json_to_sheet(rows),
            "Scholarships"
          );
          saveAs(
            new Blob([
              XLSX.write(workbook, { bookType: "xlsx", type: "array" }),
            ]),
            "scholarships.xlsx"
          );
        } else if (format === "csv") {
          const [XLSX, { saveAs }] = await Promise.all([
            import("xlsx"),
            import("file-saver"),
          ]);
          saveAs(
            new Blob(
              [XLSX.utils.sheet_to_csv(XLSX.utils.json_to_sheet(rows))],
              { type: "text/csv" }
            ),
            "scholarships.csv"
          );
        } else {
          const jsPDF = (await import("jspdf")).default;
          await import("jspdf-autotable");
          const document = new jsPDF({ orientation: "landscape" });
          const headers = Object.keys(rows[0]);
          document.text("Scholarship Applications", 14, 16);
          document.autoTable({
            head: [headers],
            body: rows.map((row) => headers.map((header) => row[header])),
            startY: 22,
            styles: { fontSize: 6 },
          });
          document.save("scholarships.pdf");
        }
      } catch (error) {
        this.showError(error, "Failed to export scholarships");
      } finally {
        this.exporting = false;
      }
    },
    printTable() {
      window.print();
    },
    showError(error, fallback) {
      Swal.fire("Error", error.response?.data?.message || fallback, "error");
    },
  },
};
</script>

<template>
  <div class="container py-4 text-start scholarship-page">
    <div
      class="d-flex justify-content-between align-items-end flex-wrap gap-3 mb-4"
    >
      <div>
        <h4 class="fw-bold mb-1">Scholarship Applications</h4>
        <p class="text-muted mb-0">
          Review and decide submitted scholarship requests.
        </p>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-sm btn-outline-secondary" @click="printTable">
          <i class="bi bi-printer me-1"></i>Print
        </button>
        <div class="dropdown">
          <button
            class="btn btn-sm btn-dark dropdown-toggle"
            data-bs-toggle="dropdown"
            :disabled="exporting"
          >
            <i class="bi bi-download me-1"></i>Export
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li>
              <button class="dropdown-item" @click="exportData('xlsx')">
                Export Excel
              </button>
            </li>
            <li>
              <button class="dropdown-item" @click="exportData('csv')">
                Export CSV
              </button>
            </li>
            <li>
              <button class="dropdown-item" @click="exportData('pdf')">
                Export PDF
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="card border-0 shadow-sm">
      <div class="card-header bg-white">
        <div class="row g-3">
          <div class="col-md-7">
            <div class="input-group">
              <span class="input-group-text bg-white"
                ><i class="bi bi-search"></i></span
              ><input
                v-model="searchQuery"
                class="form-control border-start-0"
                placeholder="Search applicant, application, scholarship, programme, or JAMB number"
              />
            </div>
          </div>
          <div class="col-md-5 d-flex justify-content-md-end">
            <div class="dropdown">
              <button
                class="btn btn-sm btn-outline-secondary dropdown-toggle"
                data-bs-toggle="dropdown"
              >
                <i class="bi bi-funnel me-1"></i>Filters
              </button>
              <div
                class="dropdown-menu dropdown-menu-end p-3 filter-menu"
                @click.stop
              >
                <div class="d-flex justify-content-between mb-3">
                  <strong>Filter Scholarships</strong
                  ><button
                    class="btn btn-link btn-sm p-0"
                    @click="clearFilters"
                  >
                    Clear all
                  </button>
                </div>
                <label class="small text-muted">Submitted Date</label>
                <div class="row g-2 mb-3">
                  <div class="col-6">
                    <input
                      v-model="dateRange.start"
                      type="date"
                      class="form-control form-control-sm"
                    />
                  </div>
                  <div class="col-6">
                    <input
                      v-model="dateRange.end"
                      type="date"
                      class="form-control form-control-sm"
                    />
                  </div>
                </div>
                <label class="small text-muted">Status</label
                ><select v-model="status" class="form-select form-select-sm">
                  <option value="">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="card-body p-0 table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th class="ps-4">Scholarship No.</th>
              <th>Name</th>
              <th>Programme</th>
              <th>JAMB Score</th>
              <th>Application No.</th>
              <th>Submitted</th>
              <th>Status</th>
              <th class="text-end pe-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="8" class="text-center py-5">
                <div class="spinner-border"></div>
              </td>
            </tr>
            <tr v-else-if="!scholarships.length">
              <td colspan="8" class="text-center py-5 text-muted">
                No scholarship applications matched the current filters.
              </td>
            </tr>
            <tr v-for="item in scholarships" v-else :key="item._id">
              <td class="ps-4">{{ item.scholarshipNumber }}</td>
              <td class="text-capitalize">
                {{ name(item)
                }}<small class="d-block text-muted">{{
                  item.userId?.email || item.submittedSnapshot?.email
                }}</small>
              </td>
              <td>{{ program(item) }}</td>
              <td>{{ item.jambScore || "N/A" }}</td>
              <td>{{ applicationNumber(item) }}</td>
              <td>{{ formatDate(item.createdAt) }}</td>
              <td>
                <span class="badge" :class="statusClass(item.status)">{{
                  statusLabel(item.status)
                }}</span>
              </td>
              <td class="text-end pe-4">
                <div class="btn-group">
                  <button
                    class="btn btn-sm btn-outline-dark"
                    @click="selected = item"
                  >
                    <i class="bi bi-eye"></i></button
                  ><button
                    v-if="canManage && item.status === 'pending'"
                    class="btn btn-sm btn-outline-success"
                    @click="decide(item, 'accepted')"
                  >
                    <i class="bi bi-check"></i></button
                  ><button
                    v-if="canManage && item.status === 'pending'"
                    class="btn btn-sm btn-outline-danger"
                    @click="decide(item, 'rejected')"
                  >
                    <i class="bi bi-x"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        class="card-footer bg-white d-flex justify-content-between align-items-center flex-wrap gap-3"
      >
        <div>
          <small>Per page</small
          ><select
            v-model.number="pagination.itemsPerPage"
            class="form-select form-select-sm d-inline-block ms-2 per-page"
          >
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
          </select>
        </div>
        <ul class="pagination pagination-sm mb-0">
          <li
            v-for="page in pageRange"
            :key="page"
            class="page-item"
            :class="{ active: page === pagination.currentPage }"
          >
            <button class="page-link" @click="load(page)">{{ page }}</button>
          </li>
        </ul>
        <small
          >Showing {{ paginationInfo.start }}-{{ paginationInfo.end }} of
          {{ paginationInfo.total }}</small
        >
      </div>
    </div>
    <div v-if="selected" class="modal-bg" @click.self="selected = null">
      <div class="details-card bg-white rounded shadow">
        <div class="p-3 border-bottom d-flex justify-content-between">
          <div>
            <h5 class="mb-0">Scholarship Application</h5>
            <small>{{ selected.scholarshipNumber }}</small>
          </div>
          <button class="btn-close" @click="selected = null"></button>
        </div>
        <div class="p-4">
          <div class="row g-3">
            <div class="col-md-6">
              <strong>Name:</strong> {{ name(selected) }}
            </div>
            <div class="col-md-6">
              <strong>Application:</strong> {{ applicationNumber(selected) }}
            </div>
            <div class="col-md-6">
              <strong>Email:</strong>
              {{ selected.userId?.email || selected.submittedSnapshot?.email }}
            </div>
            <div class="col-md-6">
              <strong>Phone:</strong>
              {{ selected.userId?.phone || selected.submittedSnapshot?.phone }}
            </div>
            <div class="col-12">
              <strong>Address:</strong> {{ selected.address || "N/A" }}
            </div>
            <div class="col-md-6">
              <strong>Programme:</strong> {{ program(selected) }}
            </div>
            <div class="col-md-6">
              <strong>Status:</strong>
              <span class="badge" :class="statusClass(selected.status)">{{
                statusLabel(selected.status)
              }}</span>
            </div>
            <div class="col-md-6">
              <strong>JAMB Registration:</strong>
              {{ selected.jambRegistrationNumber || "N/A" }}
            </div>
            <div class="col-md-6">
              <strong>JAMB Score:</strong> {{ selected.jambScore || "N/A" }}
            </div>
            <div class="col-md-6">
              <strong>Submitted:</strong> {{ formatDate(selected.createdAt) }}
            </div>
          </div>
          <div
            v-if="canManage && selected.status === 'pending'"
            class="mt-4 d-flex gap-2"
          >
            <button
              class="btn btn-success"
              @click="decide(selected, 'accepted')"
            >
              Accept</button
            ><button
              class="btn btn-danger"
              @click="decide(selected, 'rejected')"
            >
              Reject
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filter-menu {
  min-width: 330px;
}
.per-page {
  width: 80px;
}
.modal-bg {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.details-card {
  width: min(760px, 100%);
  max-height: 92vh;
  overflow: auto;
}
@media print {
  .scholarship-page button,
  .scholarship-page .card-header,
  .scholarship-page .card-footer {
    display: none !important;
  }
}
</style>
